<template>
  <div>
    <!-- Status Cards -->
    <div class="row justify-content-between mt-1 g-4 px-4">
      <div v-for="card in status_cards" :key="card.type" class="col-md-6">
        <div
          class="card text-white border-0 shadow rounded-5 overview-card-bg responsive-card h-100"
          :class="{ 'active-card': current_table === card.type }"
          @click="card.type === 'pending' ? show_pending_table() : show_submitted_table()"
          style="cursor: pointer;"
        >
          <div class="card-body d-flex align-items-center gap-3 py-3 px-3">
            <div class="circular-icon d-flex align-items-center justify-content-center flex-shrink-0 responsive-icon">
              <i :class="[card.icon, 'fa-lg', 'text-primary-blue']"></i>
            </div>
            <div class="d-flex justify-content-between align-items-center w-100">
              <div class="d-flex flex-column text-start">
                <p class="mb-1 text-xs text-white">{{ card.title }}</p>
                <p class="m-0 fw-bold text-white">{{ card.count }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Card -->
    <div class="card rounded-lg mt-3 shadow border-0 px-2 m-4">
      <!-- Header Section -->
      <div class="d-flex justify-content-between align-items-center p-3 border-bottom">
        <div>
          <p class="mb-0 fw-bold">
            <span v-if="current_table === 'pending'">Pending Master Submissions</span>
            <span v-else>Submitted Master Submissions</span>
          </p>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="d-flex gap-2 mt-2 mb-2 ms-2 justify-content-end">
        <!-- Toggle Filters Button -->
        <button
          class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark"
          @click="show_filters = !show_filters">
          {{ show_filters ? 'Hide' : 'Show' }} Filters
        </button>
      </div>

      <!-- Filters -->
      <div class="p-2" v-if="show_filters">
        <div class="row g-2">
          <div class="col-md-4">
            <input
              type="text"
              class="form-control form-control-sm"
              placeholder="Search by name or application number..."
              v-model="filters.search"
              @input="on_filters_changed({ search: $event.target.value })"
            >
          </div>
          <div class="col-md-3">
            <input
              type="date"
              class="form-control form-control-sm"
              placeholder="From date"
              v-model="filters.date_from"
              @change="on_filters_changed({ date_from: $event.target.value })"
            >
          </div>
          <div class="col-md-3">
            <input
              type="date"
              class="form-control form-control-sm"
              placeholder="To date"
              v-model="filters.date_to"
              @change="on_filters_changed({ date_to: $event.target.value })"
            >
          </div>
          <div class="col-md-2">
            <button
              class="btn btn-secondary btn-sm w-100"
              @click="filters = { search: '', date_from: '', date_to: '' }; on_filters_changed(filters);">
              Clear
            </button>
          </div>
        </div>
        <div class="mt-2 text-muted text-xs">
          Showing {{ displayed_applications_list.length }} of
          {{ current_table === 'pending' ? pending_applications_list.length : submitted_applications_list.length }} applications
        </div>
      </div>

      <!-- Applications List -->
      <template v-if="displayed_applications_list.length">
        <!-- Folder List with integrated action buttons -->
        <FolderList
          :items="displayed_applications_list"
          @view="redirect_to_application_overview"
          @view_logs="open_log_modal"
          @fetch-assigned="fetch_assigned_for_item"
          :custom-actions="true"
          :current-table="current_table"
          @submit-application="submit_application"
          @upload-letter="open_upload_modal"
          @complete-application="open_complete_modal"
          @export-application="export_application"
        />
      </template>
      <div v-else class="text-center py-5 text-muted">
        <i class="fa-solid fa-inbox fa-3x mb-3"></i>
        <p>No applications found</p>
      </div>
    </div>

    <!-- Upload Letter Modal -->
  <teleport to="body">
    <div class="modal fade" id="upload_letter_modal" tabindex="-1">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Upload Letter of Authority</h5>
            <button type="button" class="btn-close" @click="close_modal('upload_letter_modal')"></button>
          </div>
          <div class="modal-body">
            <p>Upload for: {{ selected_application?.["Entity Name"] }}</p>
            <input type="file" @change="handle_upload" accept=".pdf">
            <div v-if="upload_letter_error" class="alert alert-danger mt-2">
              {{ upload_letter_error }}
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="close_modal('upload_letter_modal')">Cancel</button>
            <button
              type="button"
              class="btn btn-primary"
              @click="upload_letter_of_authority(selected_application)"
              :disabled="is_uploading || !upload_file">
              {{ is_uploading ? 'Uploading...' : 'Upload' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </teleport>

    <!-- Complete Application Modal -->
    <teleport to="body">
      <div class="modal fade" id="complete_application_modal" tabindex="-1">
        <div class="modal-dialog">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">Complete Application</h5>
              <button type="button" class="btn-close" @click="close_modal('complete_application_modal')"></button>
            </div>
            <div class="modal-body">
              <p>Complete: {{ selected_application?.["Entity Name"] }}</p>
              <div class="form-group">
                <label>Trust Number</label>
                <input type="text" class="form-control" v-model="trust_number" placeholder="Enter trust number">
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" @click="close_modal('complete_application_modal')">Cancel</button>
              <button 
                type="button" 
                class="btn btn-primary" 
                @click="complete_application(selected_application)" 
                :disabled="is_completing || !trust_number">
                {{ is_completing ? 'Completing...' : 'Complete' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </teleport>
  </div>
</template>